/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Heading, Text, Divider } from '@towersystems/towersystems-design-system';
import page from 'page';
import { get } from 'lodash';

/**
 * Internal dependencies
 */
import Main from 'components/main';
import Navigation from 'modules/dashboard/connections/navigation';
import { requestConnection, saveConnection } from 'state/connections/actions';
import LoadingPlaceholder from 'modules/dashboard/connections/loading-placeholder';
import Card from 'components/card';
import AnnotatedSection from 'components/annotated-section';
import FormInputCheckbox from 'components/forms/form-checkbox';
import { successNotice } from 'state/notices/actions';
import ShopifyPreferences from './shopify-preferences';
import ShopifyGraphQLPreferences from './shopify-graphql-preferences';
import WooCommercePreferences from './woo-commerce-preferences';
import BigCommercePreferences from './big-commerce-preferences';
import MagentoPreferences from './magento-preferences';
import FinditPreferences from './findit-preferences';
import GunTradersPreferences from './gun-traders-preferences';
import cecSdk from 'lib/customer-ec-sdk';

/**
 * Style dependencies
 */
import './styles.scss';

const Preferences = ({ connectionId, ...props }) => {
	const [requesting, setRequesting] = useState(true);
	const [connection, setConnection] = useState(null);
	const [values, setValues] = useState({});
	const [hideResults, setHideResults] = useState(true);
	const [collectionSortOrders, setCollectionSortOrders] = useState(null);
	const [productTypes, setProductTypes] = useState(null);
	const [categoryOptions, setCategoryOptions] = useState(null);

	useEffect(() => {
		loadData(connectionId);
	}, []);

	const loadData = async (connectionId) => {
		setRequesting(true);
		try {
			let connection = await props.requestConnection(connectionId);
			setConnection(connection);
			setValues(connection.config);

			let collectionSortOrders = await cecSdk.preferences().getShopifyCollectionSortOrders();
            setCollectionSortOrders(collectionSortOrders);

			let productTypes = await cecSdk.preferences().getShopifyProductTypes();
            setProductTypes(productTypes);

			let categoryOptions = await cecSdk.preferences().getShopifyCategoryOptions();
            setCategoryOptions(categoryOptions);

		} catch (error) {
			console.log('error', error);
			page('/');
		}
		setRequesting(false);
	};

	if (true === requesting) {
		return <LoadingPlaceholder connectionId={connectionId} section='preferences' />;
	}

	// need switch statement that changes depending on connection type
	const handleOnChangeCheckbox = async (e) => {
		let n = e.target.name;
		let v = !get(values, n);

		let b = { ...values };
		let updated = { ...values, [n]: v };
		setValues(updated);

		//console.log(n+": "+v);

		try {
			if (n == 'useSmartCollections' && v == true) {
				setHideResults(true);
				await props.saveConnection(connectionId, { config: { ['useDepartmentAsProductType']: true } });
				//await props.saveConnection(connectionId, { config: { ['useCategoryAsProductType']: false  } });
			} else if (n == 'useSmartCollections' && v == false) {
				setHideResults(false);
				await props.saveConnection(connectionId, { config: { ['useDepartmentAsProductType']: false  } });
			} else if (n == 'useCategoriesSmartCollections' && v == true) {
				setHideResults(true);
				await props.saveConnection(connectionId, { config: { ['useCategoryAsProductType']: true  } });
				//await props.saveConnection(connectionId, { config: { ['useDepartmentAsProductType']: false } });
			} else if (n == 'useCategoriesSmartCollections' && v == false) {
				setHideResults(false);
				await props.saveConnection(connectionId, { config: { ['useCategoryAsProductType']: false  } });
			} 
			await props.saveConnection(connectionId, { config: { [n]: v } });
			props.successNotice('Saved');
		} catch (error) {
			setValues(b);
		}
	};

	const handleOnChangeInputBox = async (e) => {
		let n = e.target.name;
		let v = e.target.value;

		let b = { ...values };
		let updated = { ...values, [n]: v };
		setValues(updated);

		//console.log(e.target.value);

		try {
			await props.saveConnection(connectionId, { config: { [n]: v } });
			props.successNotice('Saved');
		} catch (error) {
			setValues(b);
		}
	};

	// Any new connection types will need to have their preferences render added
	const renderPreferences = (connectionType) => {
		switch (connectionType) {
			case 'shopify':
				return (
					<ShopifyPreferences
						values={values}
						saveConfigValue={handleOnChangeCheckbox}
						saveConfigValueInput={handleOnChangeInputBox}
						hideResults={hideResults}
					/>
				);
			case 'shopify_graphql':
				return (
					<ShopifyGraphQLPreferences
						values={values}
						saveConfigValue={handleOnChangeCheckbox}
						saveConfigValueInput={handleOnChangeInputBox}
						hideResults={hideResults}
						collectionSortOrders={collectionSortOrders}
						productTypes={productTypes}
						categoryOptions={categoryOptions}
					/>
				);
			case 'woo_commerce':
				return (
					<WooCommercePreferences
						values={values}
						saveConfigValue={handleOnChangeCheckbox}
						saveConfigValueInput={handleOnChangeInputBox}
					/>
				);
			case 'magento':
				return (
					<MagentoPreferences
						values={values}
						saveConfigValue={handleOnChangeCheckbox}
						saveConfigValueInput={handleOnChangeInputBox}
					/>
				);
			case 'big_commerce':
				return (
					<BigCommercePreferences
						values={values}
						saveConfigValue={handleOnChangeCheckbox}
						saveConfigValueInput={handleOnChangeInputBox}
					/>
				);
			case 'findit':
				return (
					<FinditPreferences
						values={values}
						saveConfigValue={handleOnChangeCheckbox}
						saveConfigValueInput={handleOnChangeInputBox}
					/>
				);
			case 'gun_traders':
				return (
					<GunTradersPreferences
						values={values}
						saveConfigValue={handleOnChangeCheckbox}
						saveConfigValueInput={handleOnChangeInputBox}
					/>
				);
		}
		return null;
	};

	return (
		<Main>
			<Navigation connectionId={connectionId} section='preferences' />
			<Box paddingTop='xxlarge' paddingBottom='xxlarge'>
				<Box display='flex' justifyContent='spaceBetween' alignItems='center' paddingBottom='xlarge'>
					<Heading level='1'>Preferences</Heading>
				</Box>
				<Divider />
			</Box>
			<Stack space='medium'>{renderPreferences(get(connection, 'connection_type_code'))}</Stack>
		</Main>
	);
};

export default connect((state, ownProps) => ({}), {
	requestConnection,
	successNotice,
	saveConnection,
})(Preferences);
